import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck, Zap, Globe, Layers, ArrowRight, LineChart, Lock, Cpu, Wallet, TrendingUp,
} from "lucide-react";
import { PublicLayout } from "@/components/PublicLayout";
import { SectionHeading } from "@/components/shared";

const HERO_IMG = "https://images.unsplash.com/photo-1518980120692-3cfe64c152d0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const features = [
  { icon: Cpu, title: "Algorithmic Infrastructure", desc: "Automated market monitoring and structured trade execution built on modern data pipelines." },
  { icon: ShieldCheck, title: "Security First", desc: "Role-based access, server-side balance controls, audit logging and encrypted document handling." },
  { icon: Wallet, title: "Multi-Network Deposits", desc: "Fund your account seamlessly using USDT on TRC20 and BNB Smart Chain (BEP20)." },
  { icon: LineChart, title: "Transparent Reporting", desc: "Every deposit, withdrawal and reward is tracked with clear transaction history." },
  { icon: Globe, title: "Global Access", desc: "A responsive platform accessible from mobile, tablet and desktop, anywhere." },
  { icon: Lock, title: "Verified Accounts", desc: "Optional KYC verification workflow for enhanced account security and compliance." },
];

const steps = [
  { n: "01", t: "Create Account", d: "Register with your details and secure your account in seconds." },
  { n: "02", t: "Fund Wallet", d: "Deposit USDT via TRC20 or BEP20 and submit your transaction for review." },
  { n: "03", t: "Select a Plan", d: "Choose a plan that matches your budget from 5 to 50 USDT." },
  { n: "04", t: "Track Activity", d: "Monitor balance, earnings and withdrawals from your dashboard." },
];

export default function Home() {
  return (
    <PublicLayout>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050811]/70 via-[#050811]/85 to-[#050811]" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-20 pb-28 lg:pt-28 lg:pb-36">
          <div className="max-w-3xl">
            <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass text-xs font-mono uppercase tracking-[0.2em] text-cyan-300 mb-6">
              <Zap className="w-3.5 h-3.5" /> Next-Generation Digital Asset Technology
            </motion.p>
            <motion.h1 initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              <span className="text-white">AI </span><span className="text-gradient text-glow">TECHNOLOGY</span>
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
              className="mt-5 text-lg sm:text-xl text-slate-300 font-medium">
              Next-Generation Digital Asset Technology Platform
            </motion.p>
            <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
              className="mt-4 text-base text-slate-400 leading-relaxed max-w-2xl">
              A secure, modern platform for managing digital asset deposits, withdrawals and referral activity.
              Built with an institutional-grade interface and transparent, server-side controls. Participation in
              digital assets involves risk; returns are variable and never guaranteed.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
              className="mt-9 flex flex-wrap gap-4">
              <Link to="/signup" data-testid="hero-create-account-btn"
                className="btn-glow inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-slate-950">
                Create Account <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/login" data-testid="hero-login-btn"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-slate-100 glass glass-hover">
                Login
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <SectionHeading eyebrow="Why AI TECHNOLOGY" title="A platform engineered for clarity and control"
          subtitle="Every feature is designed around transparency, security and a premium user experience." />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className="glass glass-hover rounded-2xl p-6 lg:p-7">
              <div className="w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mb-5">
                <f.icon className="w-6 h-6 text-cyan-300" />
              </div>
              <h3 className="font-heading text-lg text-white mb-2">{f.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <SectionHeading center eyebrow="How It Works" title="Get started in four steps" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((s) => (
            <div key={s.n} className="glass rounded-2xl p-6 relative overflow-hidden">
              <span className="font-mono text-5xl font-bold text-cyan-500/15 absolute top-3 right-4">{s.n}</span>
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center text-slate-950 font-bold mb-4">{s.n}</div>
              <h3 className="font-heading text-white mb-2">{s.t}</h3>
              <p className="text-sm text-slate-400">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="glass rounded-3xl p-10 lg:p-16 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 to-blue-600/5" />
          <div className="relative">
            <TrendingUp className="w-12 h-12 text-cyan-400 mx-auto mb-5" />
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold text-white">Ready to begin?</h2>
            <p className="text-slate-400 mt-4 max-w-lg mx-auto">Create your AI TECHNOLOGY account and access a secure, transparent digital asset dashboard.</p>
            <Link to="/signup" className="btn-glow inline-flex items-center gap-2 px-8 py-3.5 rounded-full font-semibold text-slate-950 mt-8">
              Create Account <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
